import { useState, useLayoutEffect } from 'react';
import ReviewImage from './ReviewImage';
import CommentRatingStar from './CommentRatingStar';
import useStyles from './useStyles';

import { CustomizedTypography } from '@components/Customized';
import { clsx } from '@components/Provider';

import {
  ClickAwayListener,
  IconButton,
  Popper,
  useMediaQuery,
  useTheme
} from '@material-ui/core';

import { ChevronDown, Edit, OverflowMenuHorizontal } from '@carbon/icons-react';
import { CapaRfqReview, CapaRfqReviewType } from '@generated/graphql';
import { formatDate } from '@utils/formatter';
import getPriceRange from '../getPriceRange';
import { requestEnumToString } from '@utils/enumToString';
import { getFileUrl } from '@utils/urls';
import useReview from '@hooks/useReview';

interface Props {
  index: number;
  review?: CapaRfqReview;
}

const WrittenReviewCommentItem = ({ index, review }: Props) => {
  const classes = useStyles();
  const [elipsis, setElipsis] = useState(false);
  const [more, setMore] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const matches = useMediaQuery(useTheme().breakpoints.down('xs'));

  const reviewFiles = review?.reviewFiles ?? [];

  const isEditable = review?.isEditable;

  const images = reviewFiles.map((file) => getFileUrl(file?.fileId ?? ''));

  const { onUpdateReview } = useReview();

  const noImage = images.length === 0;

  const multipleImages = images.length > 1;

  const handleImageExpand = (value: boolean) => {
    setMore(value);
  };

  const isRenderMoreButton = () => {
    if (noImage) {
      // 이미지가 없으면
      return elipsis; //3줄 이상일때는 렌더
    } else {
      // 이미지가 있을때

      if (matches) {
        // 모바일 O
        if (images.length === 1 && elipsis) return true;
        if (images.length === 1 && !elipsis) return false;

        if (images.length > 1) return true;

        return elipsis;
      } else {
        // 모바일 X
        if (images.length >= 1) return true;

        return elipsis;
      }
    }
  };

  useLayoutEffect(() => {
    const textContents = document.getElementById(`text__contents-${index}`);

    const scrollHeight = textContents?.scrollHeight ?? 0;
    const clientHeight = textContents?.clientHeight ?? 0;

    if (scrollHeight > clientHeight) {
      setElipsis(true);
    }
  }, [index]);

  return (
    <div className={classes.item} id={review?.id}>
      <ClickAwayListener
        mouseEvent="onMouseDown"
        onClickAway={() => setMenuAnchor(null)}
      >
        <Popper
          anchorEl={menuAnchor}
          open={menuOpen}
          disablePortal
          placement={'bottom-end'}
        >
          <div
            className={classes.popover}
            onClick={() => {
              onUpdateReview({
                open: true,
                rfqId: '',
                quotationPartnerId: '',
                reviewId: review?.id ?? '',
                profileId: review?.profileId ?? '',
                projectName: review?.projectName ?? '',
                organizationName: review?.organizationName ?? '',
                rfqReviewType: CapaRfqReviewType.OnlyTransaction,
                transactionSatisfactionPoint: review?.reviewPoint,
                transactionSatisfactionComment: review?.reviewComment,
                reviewFiles: reviewFiles.map(({ fileId, fileName }) => ({
                  fileId: fileId ?? '',
                  fileName: fileName ?? ''
                })),
                inDueDate: review?.indueDate
              });
            }}
          >
            <Edit size={20} />
            <span>리뷰 수정하기</span>
          </div>
        </Popper>
      </ClickAwayListener>

      {isEditable && (
        <IconButton
          className={classes.menu}
          onClick={(e) => {
            setMenuAnchor(e.currentTarget);
          }}
        >
          <OverflowMenuHorizontal size={20} />
        </IconButton>
      )}
      <div className={classes.company}>
        <div className={classes.rating}>
          <CommentRatingStar rating={review?.reviewPoint ?? 0} />
          <CustomizedTypography
            className="createdAt"
            color="textSecondary"
            variant="body2"
          >
            {review?.reviewedAt && formatDate(review?.reviewedAt, 'YY.MM.DD')}
          </CustomizedTypography>
        </div>

        <CustomizedTypography
          variant="body2"
          fontWeight="500"
          gutterBottoms={2}
        >
          {review?.organizationName}
        </CustomizedTypography>
        <CustomizedTypography
          variant="subtitle2"
          color="textSecondary"
          gutterBottoms={2}
        >
          발주액 {getPriceRange(review?.purchaseOrderValue ?? 0)}&nbsp;
          {review?.indueDate ? `· 납기일 준수` : `· 납기일 미준수`}
        </CustomizedTypography>
        <CustomizedTypography variant="subtitle2" color="textSecondary">
          {requestEnumToString(review?.serviceType)} · {review?.projectName}
        </CustomizedTypography>
      </div>

      <div className={classes.contents}>
        <div
          className={clsx('inner__contents', {
            'inner__contents-more': more,
            'inner__contents-no_img': noImage
          })}
        >
          <CustomizedTypography
            id={`text__contents-${index}`}
            className={clsx('breakAll', {
              more: more,
              elipsis: elipsis && !more
            })}
            variant="body1"
            color="textPrimary"
            fontWeight="500"
          >
            {review?.reviewComment}
          </CustomizedTypography>

          {review?.reviewComment && isRenderMoreButton() && (
            <div
              className={classes.more}
              onClick={() => setMore((prev) => !prev)}
            >
              <CustomizedTypography
                variant="body2"
                color="textPrimary"
                fontWeight="500"
              >
                {more ? '접기' : '더보기'}
              </CustomizedTypography>
              <ChevronDown
                size={20}
                className={more ? 'animation' : undefined}
              />
            </div>
          )}
        </div>

        {!matches && !noImage && (
          <>
            {!more && (
              <ReviewImage
                matches={matches}
                mode="preview"
                src={images[0]}
                max={images.length}
                onClick={() => handleImageExpand(!more)}
              />
            )}
          </>
        )}
      </div>

      {!matches && !noImage && (
        <>
          {more &&
            images.map((img, index) => (
              <ReviewImage
                key={img}
                index={index}
                matches={matches}
                max={images.length}
                mode="more"
                gutterBottom={index !== images.length - 1}
                src={img}
              />
            ))}
        </>
      )}

      {matches && !noImage && (
        <>
          {multipleImages ? (
            <>
              {!more && (
                <div className={classes.images}>
                  <ReviewImage
                    className="left__radius"
                    matches={matches}
                    mode="preview"
                    max={images.length}
                    src={images[0]}
                  />

                  <ReviewImage
                    className="right__radius"
                    matches={matches}
                    mode="preview"
                    src={images[1]}
                    multipleImages
                    max={images.length}
                    onClick={() => handleImageExpand(!more)}
                  />
                </div>
              )}

              {more &&
                images.map((img, index) => (
                  <ReviewImage
                    key={img}
                    index={index}
                    max={images.length}
                    matches={matches}
                    mode="more"
                    gutterBottom={index !== images.length - 1}
                    src={img}
                  />
                ))}
            </>
          ) : (
            <ReviewImage
              matches={matches}
              mode="more"
              src={images[0]}
              max={images.length}
            />
          )}
        </>
      )}
    </div>
  );
};

export default WrittenReviewCommentItem;
